'use client';

/**
 * ContactInfoCard — inline contact-details capture form, built on the shared
 * StandardCard shell (see primitives/standard-card.tsx). Originally the
 * pre-handoff contact form (Decision #17, 2026-07-hubspot-native-handoff.md)
 * and still used exactly that way today; now parameterized (`title`/
 * `description`/`submitLabel`/`fields`) so a second, non-handoff caller
 * (e.g. capturing details after a tool timeout) can reuse the same form
 * mechanics — prefill/reset wiring, validation, a11y — without forking this
 * file. Every prop defaults to the pre-handoff form's original copy/shape,
 * so the live handoff call site (page.tsx) renders identically whether or
 * not it opts into the new props.
 *
 * Renders INLINE at the bottom of the message list (see page.tsx — inside
 * ConversationArea, after the messages.map) rather than as a modal: for the
 * live handoff, Sara stays active and the input bar keeps routing to her
 * while this card is up (uiState stays CHAT — see the
 * CONTACT_INFO_REQUESTED case in useAssistantConnection.ts). This is
 * deliberately a full form card, not a conversational back-and-forth:
 * guaranteed capture must not depend on LLM prompt adherence (that's the
 * whole reason the handoff decision exists — see the Decision #17
 * "Trigger" note in the plan).
 *
 * `fields` dictates which inputs render, their required-ness, and their
 * prefills — this component hardcodes no policy about which fields exist or
 * are required, only display labels/types and generic shape validation
 * (contact-form-validation.ts). It defaults to DEFAULT_CONTACT_FIELDS
 * (Name/Email required, Phone optional) reproducing the original pre-handoff
 * shape; the live handoff call site always passes its own server-driven
 * array and is unaffected by this default.
 *
 * Motion: the card slides up + fades in with a soft field cascade on entry,
 * and COLLAPSES (height → 0) on exit — callers that need the exit to play
 * wrap the conditional render in <AnimatePresence> (see page.tsx). Paired
 * with ContactSummaryCard's expand-in (which enters just above, with a
 * slight delay) for the live handoff, the submit reads as the form folding
 * into the receipt. Honors prefers-reduced-motion via useReducedMotion
 * (house pattern — see also contact-summary-card.tsx). StandardCard itself
 * is a plain presentational shell with no animation capability, so — same
 * as ContactSummaryCard — this motion lives in a wrapping <motion.div>
 * around it rather than inside the primitive.
 *
 * ── Two layouts, one form (design Turn 21h) ──────────────────────────────
 *
 * `layout` picks the CHROME; everything that can drift — values, validation,
 * the reset-on-new-request effect, the submit payload — is shared, so the
 * two surfaces cannot disagree about what a valid submission is. That is the
 * same rule useTimeoutRecovery follows, and for the same reason: a second
 * copy of a stateful form is a divergence bug waiting to happen.
 *
 *   'card' (default) — today's inline transcript card on the full page.
 *   'step'           — the embedded panel's full-height form step. 21h
 *                      measured the card rendered in a 400×620 panel at
 *                      766px of content and prescribed subtraction, not
 *                      scrolling: no card wrapper, no avatar indent, no
 *                      separate privacy row, errors on the label row instead
 *                      of under the field, and the action welded to the
 *                      bottom edge. That lands at 479px.
 *
 * CONTRACT for `layout="step"` — the host, not this component, owns:
 *   1. the slot. Give it a flex COLUMN parent with `min-h-0` and a bounded
 *      height. The step is `h-full` and pins its own footer; in an unbounded
 *      parent the footer has no edge to pin to.
 *   2. the ONLY other scroll container in the panel. The step's field list is
 *      `overflow-y-auto`; if an ancestor also scrolls, the panel scrolls the
 *      whole form out from under the footer and the user loses the button.
 *   3. hiding the message input bar, and providing the escape hatch — the
 *      step deliberately has no Cancel of its own (21h rule 6 puts it in the
 *      presence ribbon, which is structurally guaranteed during COLLECTING).
 *      `onCancel` is still required because 'card' renders it.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ContactField } from '@/types/chat';
import {
  validateContactForm,
  type ContactFormValues,
} from '@/lib/contact-form-validation';
import { StandardCard } from './primitives/standard-card';

/** Reproduces the original pre-handoff form's field shape (Name/Email
 *  required, Phone optional) for callers that don't need per-field
 *  server-driven control. The live handoff call site always supplies its
 *  own `fields` (from the `contact_info_requested` WS event) and never
 *  exercises this default. */
export const DEFAULT_CONTACT_FIELDS: ContactField[] = [
  { name: 'name', required: true },
  { name: 'email', required: true },
  { name: 'phone', required: false },
];

/** Which chrome the form wears — see the "Two layouts, one form" note above
 *  for the contract each one imposes on its host. */
export const CONTACT_INFO_LAYOUT = {
  /** Inline transcript card on the full page. */
  CARD: 'card',
  /** Full-height panel step (design 21h) — the embedded panel. */
  STEP: 'step',
} as const;
export type ContactInfoLayout =
  (typeof CONTACT_INFO_LAYOUT)[keyof typeof CONTACT_INFO_LAYOUT];

/** Per-layout copy. The step's heading is shorter because 21h's budget gives
 *  the title block 66px at 368px wide — the card's "A few details before a
 *  guide joins" wraps to three lines there — and its subline carries the
 *  reassurance the deleted Sara preamble bubble used to. An explicit `title`/
 *  `description` prop still wins over both. */
const LAYOUT_COPY: Record<
  ContactInfoLayout,
  { title: string; description?: string }
> = {
  [CONTACT_INFO_LAYOUT.CARD]: {
    title: 'A few details before a guide joins',
  },
  [CONTACT_INFO_LAYOUT.STEP]: {
    title: 'A few details first',
    description: 'The agent will already have our conversation.',
  },
};

interface ContactInfoCardProps {
  /** Field list — dictates which inputs render, in this order, with these
   *  required/prefill values. A NEW array reference here means a fresh (or
   *  re-requested/corrected) request landed — see the reset effect below.
   *  Defaults to DEFAULT_CONTACT_FIELDS. */
  fields?: ContactField[];
  /** Submit handler — called only once client-side validation passes. */
  onSubmit: (values: { name: string; email: string; phone?: string }) => void;
  /** Cancel handler — the banner-equivalent "End chat" affordance for the
   *  live handoff phase; other callers can wire this to whatever "back out
   *  of this form" action fits their flow. */
  onCancel: () => void;
  /** Card heading. Defaults to the layout's own copy (LAYOUT_COPY). */
  title?: ReactNode;
  /** Optional explanatory copy rendered between the heading and the fields.
   *  Omitted by default on 'card' — the original pre-handoff card has no
   *  subcopy; 'step' defaults to 21h's subline. */
  description?: ReactNode;
  /** Submit button label shown while not submitting (the "Connecting…"
   *  in-flight state is unaffected). Defaults to 'Connect me'. */
  submitLabel?: string;
  /** Chrome variant. Defaults to 'card' so every existing call site is
   *  untouched. See the "Two layouts, one form" note for the host contract
   *  that 'step' imposes. */
  layout?: ContactInfoLayout;
}

const FIELD_LABEL: Record<ContactField['name'], string> = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
};

const FIELD_INPUT_TYPE: Record<ContactField['name'], string> = {
  name: 'text',
  email: 'email',
  phone: 'tel',
};

const FIELD_AUTOCOMPLETE: Record<ContactField['name'], string> = {
  name: 'name',
  email: 'email',
  phone: 'tel',
};

function buildInitialValues(fields: ContactField[]): ContactFormValues {
  const values: ContactFormValues = { name: '', email: '', phone: '' };
  for (const field of fields) {
    values[field.name] = field.prefill ?? '';
  }
  return values;
}

export function ContactInfoCard({
  fields = DEFAULT_CONTACT_FIELDS,
  onSubmit,
  onCancel,
  title,
  description,
  submitLabel = 'Connect me',
  layout = CONTACT_INFO_LAYOUT.CARD,
}: ContactInfoCardProps) {
  const [values, setValues] = useState<ContactFormValues>(() =>
    buildInitialValues(fields)
  );
  const [touched, setTouched] = useState<Partial<Record<string, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const reducedMotion = useReducedMotion();

  // A fresh contact_info_requested — a new handoff, OR a server-driven
  // re-request/correction for the SAME handoff (Decision #17's "server owns
  // the correction loop") — always arrives as a brand-new `fields` array
  // reference (see buildCollectingHandoff in useHandoff.ts), never mutated in
  // place. So this effect fires exactly when the card should reset: re-apply
  // the event's own prefills and re-enable the inputs/button. It does NOT
  // fire on unrelated parent re-renders, since `fields` otherwise keeps its
  // identity across those.
  useEffect(() => {
    setValues(buildInitialValues(fields));
    setTouched({});
    setSubmitting(false);
  }, [fields]);

  const errors = validateContactForm(fields, values);

  const handleBlur = (name: string) =>
    setTouched((prev) => ({ ...prev, [name]: true }));

  const handleChange = (name: ContactField['name'], value: string) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Reveal every field's validation state on submit, not just blurred ones
    // — a user who never tabbed through a required field should still see
    // why the submit button didn't proceed.
    setTouched(Object.fromEntries(fields.map((f) => [f.name, true])));
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    onSubmit({
      name: values.name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim() || undefined,
    });
  };

  const heading = title ?? LAYOUT_COPY[layout].title;
  const subcopy = description ?? LAYOUT_COPY[layout].description;

  // Shared per-field view model so the two layouts can never disagree about
  // which field is in an error state or what that error says — they differ
  // only in where they PUT it.
  const fieldViews = fields.map((field) => {
    const inputId = `contact-info-${field.name}`;
    return {
      field,
      inputId,
      errorId: `${inputId}-error`,
      error: errors[field.name],
      hasError: !!touched[field.name] && !!errors[field.name],
      // Distinguishes "you skipped this" from "this doesn't look right",
      // which the step layout needs: it compresses the former to a REQUIRED
      // chip (21h's mock) and shows the latter's message verbatim, because
      // "REQUIRED" would be an outright lie about a malformed email.
      isEmptyRequired: field.required && values[field.name].trim().length === 0,
    };
  });

  if (layout === CONTACT_INFO_LAYOUT.STEP) {
    return (
      <ContactInfoStep
        heading={heading}
        subcopy={subcopy}
        submitLabel={submitLabel}
        submitting={submitting}
        fieldViews={fieldViews}
        values={values}
        onFieldChange={handleChange}
        onFieldBlur={handleBlur}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <motion.div
      // Entry: rise + settle. Exit: collapse to nothing so the transcript
      // closes the gap smoothly and (for the live handoff) the receipt card
      // entering just above, slightly delayed — see ContactSummaryCard —
      // reads as what the form folded into. overflow-hidden is required for
      // the height collapse.
      initial={
        reducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }
      }
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={
        reducedMotion
          ? { opacity: 0, transition: { duration: 0.15 } }
          : {
              opacity: 0,
              height: 0,
              scale: 0.97,
              marginTop: 0,
              transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
            }
      }
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="overflow-hidden"
    >
      <StandardCard
        icon={<User aria-hidden="true" className="size-[15px]" />}
        title={heading}
      >
        <div className="flex flex-col gap-[14px]">
          {subcopy && (
            <div className="text-[13.5px]" style={{ color: '#8a7c68' }}>
              {subcopy}
            </div>
          )}
          <form
            role="form"
            aria-label="Contact information"
            onSubmit={handleSubmit}
            // Without this, the browser's own type="email" format
            // constraint intercepts an invalid address at submit time and
            // silently cancels the submit event before handleSubmit ever
            // runs — our custom styled error (and its role="alert") would
            // never render. Pre-existing gap (predates this migration);
            // caught by the new validation test below. All validation is
            // deliberately owned by contact-form-validation.ts instead.
            noValidate
            className="grid grid-cols-1 gap-3.5 sm:grid-cols-2"
          >
            {fieldViews.map(
              ({ field, inputId, errorId, error, hasError }, index) => {
                return (
                  <motion.div
                    key={field.name}
                    // Gentle cascade under the card's own entrance — one beat
                    // per field. Skipped entirely under prefers-reduced-motion.
                    initial={reducedMotion ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: 0.08 + index * 0.06,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="flex flex-col gap-1"
                  >
                    <label
                      htmlFor={inputId}
                      className="font-['Work_Sans'] text-[12.5px] font-semibold text-[#8a7c68]"
                    >
                      {FIELD_LABEL[field.name]}
                      {!field.required && (
                        <span className="ml-1 font-normal text-[#b3a996]">
                          (optional)
                        </span>
                      )}
                    </label>
                    <input
                      id={inputId}
                      type={FIELD_INPUT_TYPE[field.name]}
                      autoComplete={FIELD_AUTOCOMPLETE[field.name]}
                      value={values[field.name]}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      onBlur={() => handleBlur(field.name)}
                      disabled={submitting}
                      aria-invalid={hasError}
                      aria-describedby={hasError ? errorId : undefined}
                      className={cn(
                        // T5 input treatment: warm hairline + soft warm fill,
                        // 11px radius (not pill) per the design's form fields.
                        'rounded-[11px] border-[1px] bg-[#FEFCF9] px-3.5 py-[11px]',
                        "font-['Work_Sans'] text-[14.5px] text-[#31364e]",
                        'placeholder:text-[#b3a996]',
                        'transition-[border-color,box-shadow] duration-200',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
                        hasError
                          ? 'border-[#b3261e]'
                          : 'border-[#e6dccd] focus-visible:border-[#e36001]/60',
                        'disabled:opacity-50'
                      )}
                    />
                    {hasError && (
                      <span
                        id={errorId}
                        role="alert"
                        className="font-['Work_Sans'] text-[12px] text-[#b3261e]"
                      >
                        {error}
                      </span>
                    )}
                  </motion.div>
                );
              }
            )}

            {/* Stacked below sm: side-by-side, the disclosure note gets
                squeezed to a ~110px column and wraps into four lines (FB26). */}
            <div className="mt-1.5 flex flex-col gap-[12px] sm:col-span-2 sm:flex-row sm:items-center sm:justify-between sm:gap-[16px]">
              {/* T5 disclosure: lock + reassurance copy, left of the actions */}
              <span className="flex min-w-0 items-center gap-1.5 font-['Work_Sans'] text-[12.5px] leading-snug text-[#9a8f7f]">
                <svg
                  aria-hidden="true"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Only shared with our support team.
              </span>
              <div className="flex shrink-0 items-center justify-end gap-[16px]">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={submitting}
                  className={cn(
                    "font-['Work_Sans'] text-[14px] font-semibold text-[#8a7c68]",
                    'transition-colors hover:text-[#31364e] disabled:opacity-50'
                  )}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={cn(
                    'rounded-[22px] px-[22px] py-[11px] text-white',
                    "font-['Work_Sans'] text-[14.5px] font-semibold",
                    'transition-[background-color,transform] duration-200',
                    submitting
                      ? 'cursor-not-allowed bg-[#e36001]/60'
                      : 'bg-[#e36001] hover:bg-[#f26a0c] active:scale-[0.98]'
                  )}
                >
                  {submitting ? (
                    <span className="animate-pulse">Connecting…</span>
                  ) : (
                    submitLabel
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </StandardCard>
    </motion.div>
  );
}

/** One field's resolved display state — computed once in ContactInfoCard so
 *  both layouts read the SAME answer to "is this field in error, and why". */
interface ContactFieldView {
  field: ContactField;
  inputId: string;
  errorId: string;
  error?: string;
  hasError: boolean;
  isEmptyRequired: boolean;
}

interface ContactInfoStepProps {
  heading: ReactNode;
  subcopy?: ReactNode;
  submitLabel: string;
  submitting: boolean;
  fieldViews: ContactFieldView[];
  values: ContactFormValues;
  onFieldChange: (name: ContactField['name'], value: string) => void;
  onFieldBlur: (name: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

/**
 * The 21h form step — private to this module and deliberately STATELESS.
 * Every value, error and handler is passed in from ContactInfoCard, so this
 * is chrome only; there is exactly one contact form in the codebase and this
 * is one of its two faces.
 *
 * Three structural rules from 21h are load-bearing here, not cosmetic:
 *   · the field list is the ONLY `overflow-y-auto` node (rule 4) — that is
 *     what keeps the submit button on screen at every panel height;
 *   · errors render on the LABEL ROW, in space that is already reserved
 *     (rule 5) — an error under the input would grow the field and re-scroll
 *     the list while the user is still typing in it;
 *   · there is no Cancel (rule 6) — the presence ribbon owns the exit, so
 *     the footer stays a single-action 113px block.
 */
function ContactInfoStep({
  heading,
  subcopy,
  submitLabel,
  submitting,
  fieldViews,
  values,
  onFieldChange,
  onFieldBlur,
  onSubmit,
}: ContactInfoStepProps) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-[#FFFAF5]">
      {/* Title block. Carries the reassurance the deleted Sara preamble
          bubble used to — 66px here against that bubble's 78 plus a 50px
          card wrapper. */}
      <div className="flex shrink-0 items-start gap-[10px] px-[16px] pb-[12px] pt-[16px]">
        <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-[#FBEBD8]">
          <User aria-hidden="true" className="size-[15px] text-[#e36001]" />
        </span>
        <div className="min-w-0">
          <div className="font-[family-name:var(--font-lora)] text-[15.5px] font-semibold leading-[1.3] text-[#31364e]">
            {heading}
          </div>
          {subcopy && (
            <div className="text-[12px] leading-[1.5] text-[#8a7c68]">
              {subcopy}
            </div>
          )}
        </div>
      </div>

      <form
        role="form"
        aria-label="Contact information"
        onSubmit={onSubmit}
        // Same reason as the card layout: the browser's own type="email"
        // constraint would otherwise cancel submit before our validation
        // (and its role="alert") ever runs.
        noValidate
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-[12px] overflow-y-auto px-[16px]">
          {fieldViews.map(
            ({ field, inputId, errorId, error, hasError, isEmptyRequired }) => (
              <div key={field.name} className="flex flex-col gap-[5px]">
                {/* Label row. The status slot is always present in the
                    layout, so switching between "Optional", nothing, and an
                    error changes zero pixels of height. */}
                <div className="flex items-baseline gap-[8px]">
                  <label
                    htmlFor={inputId}
                    className="font-['Work_Sans'] text-[11px] font-bold uppercase tracking-[0.05em] text-[#8a7c68]"
                  >
                    {FIELD_LABEL[field.name]}
                  </label>
                  {hasError ? (
                    <span
                      id={errorId}
                      role="alert"
                      className="ml-auto min-w-0 truncate font-['Work_Sans'] text-[11px] font-bold tracking-[0.03em] text-[#b3261e]"
                    >
                      {isEmptyRequired ? (
                        <>
                          {/* Sighted users get 21h's compact chip; screen
                              readers get the full sentence off the same
                              node, so aria-describedby stays truthful
                              without costing a pixel. */}
                          <span aria-hidden="true">REQUIRED</span>
                          <span className="sr-only">{error}</span>
                        </>
                      ) : (
                        error
                      )}
                    </span>
                  ) : (
                    !field.required && (
                      <span className="ml-auto shrink-0 font-['Work_Sans'] text-[11px] font-semibold text-[#b3a996]">
                        Optional · for a call back
                      </span>
                    )
                  )}
                </div>
                <input
                  id={inputId}
                  type={FIELD_INPUT_TYPE[field.name]}
                  autoComplete={FIELD_AUTOCOMPLETE[field.name]}
                  value={values[field.name]}
                  onChange={(e) => onFieldChange(field.name, e.target.value)}
                  onBlur={() => onFieldBlur(field.name)}
                  disabled={submitting}
                  aria-invalid={hasError}
                  aria-describedby={hasError ? errorId : undefined}
                  className={cn(
                    'min-h-[44px] rounded-[12px] border-[1px] bg-white px-[13px]',
                    "font-['Work_Sans'] text-[14.5px] text-[#31364e]",
                    'placeholder:text-[#b3a996]',
                    'transition-[border-color,box-shadow] duration-200',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
                    // Rose, not the card's saturated #b3261e: at this size the
                    // REQUIRED chip is already carrying the alarm, and a full
                    // red outline on a 400px panel reads as a failed page.
                    hasError
                      ? 'border-[#dcb3ab]'
                      : 'border-[#e6dccd] focus-visible:border-[#e36001]/60'
                  )}
                />
              </div>
            )
          )}
        </div>

        {/* Pinned footer — shrink-0 against the field list's flex-1, so it
            keeps its edge no matter how many fields the server asks for. */}
        <div className="flex shrink-0 flex-col gap-[8px] border-t border-[#f0e7da] bg-white px-[16px] pb-[14px] pt-[12px]">
          <button
            type="submit"
            disabled={submitting}
            className={cn(
              'flex min-h-[46px] w-full items-center justify-center rounded-[23px] text-white',
              "font-['Work_Sans'] text-[15px] font-semibold",
              'shadow-[0_10px_22px_-12px_rgba(227,96,1,0.7)]',
              'transition-[background-color,transform] duration-200',
              submitting
                ? 'cursor-not-allowed bg-[#e36001]/60'
                : 'bg-[#e36001] hover:bg-[#f26a0c] active:scale-[0.99]'
            )}
          >
            {submitting ? (
              <span className="animate-pulse">Connecting…</span>
            ) : (
              submitLabel
            )}
          </button>
          {/* Both halves of the deleted blocks land here: the privacy line
              that had its own 34px row, and the "you can keep typing"
              promise the ribbon used to make — restated accurately, since
              the input bar is hidden while this step is open. */}
          <div className="flex items-start gap-[6px] font-['Work_Sans'] text-[11px] leading-[1.45] text-[#9a8f7f]">
            <svg
              aria-hidden="true"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mt-[2px] shrink-0"
            >
              <rect x="4" y="10" width="16" height="10" rx="2" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            Only shared with our support team · you can keep chatting after
            this.
          </div>
        </div>
      </form>
    </div>
  );
}
